import { useNavigate } from 'react-router-dom';
import { LogIn, ArrowRight } from '@/components/ui/icons';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import TextAreaChat from '@/components/TextAreaChat';
import { ParameterArea } from '@/components/ParameterArea';
import { useState, useMemo, useEffect, lazy, Suspense } from 'react';
import { MessageItem } from '../types/misc.ts';
import { LimitReachedMessage } from '@/components/LimitReachedMessage';
import { LowPromptsWarningMessage } from '@/components/LowPromptsWarningMessage';
import { useIsMobile } from '@/hooks/useIsMobile';
import { cn } from '@/lib/utils';
import { SelectedItemsContext } from '@/contexts/SelectedItemsContext';
import { useHomepage } from '@/contexts/homepage-context';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { INLET_LABELS } from '@/config/supin';
import { SUSP_TYPE_LABELS } from '@/config/shark';
import { IMPLICIT_PRIMITIVE_LABELS } from '@/config/implicitPrimitives';
import { ViewerRouteFallback } from '@/components/ViewerRouteFallback';
import { PromptHero } from './prompt/PromptHero';
import { PromptCard } from '@/components/promptCard/PromptCard';
import { FeatureWindowHost } from '@/components/viewer/featureWindow/FeatureWindowHost';
import {
  SupinLicenseAccessBox,
  SupinLicenseSlideshow,
} from './prompt/SupinLicenseGate';
import { PromptLogo, PromptSubtitle } from './prompt/PromptHeader';
import { useGenerateConversation } from './prompt/useGenerateConversation';
import { usePromptSidebarActions } from './prompt/usePromptSidebarActions';

// Lazy-loaded so StaticStlPreview → RTXviewport → ThreeScene stays behind the
// Lazy3D boundary (a top-level import here would re-bloat first paint with the
// whole WebGL viewer — see the Lazy3D note in CLAUDE.md). Only the NASA Unitcell
// Lattice Converter (a niche Text-to-CAD sub-tab) ever mounts it.
const LatticeConverterViewport = lazy(() =>
  import('./prompt/LatticeConverterViewport').then((m) => ({
    default: m.LatticeConverterViewport,
  })),
);

// Lazy for the same reason: the kinematics workbench pulls the R3F/three stack
// (LinkageScene). A static import here kept the `three` chunk modulepreloaded
// on the LANDING page — the third edge of the A2 Lazy3D leak (the other two
// were the tab shell's EditorView/FilesView imports; see views/lazyViews.tsx).
const SuspensionKinematics = lazy(
  () => import('@/components/SuspensionKinematics'),
);
// Land → Spaceframe Chassis: a live configurator (viewer + feature-tree
// windows), lazy for the same Lazy3D reason.
const SpaceframeWorkbenchView = lazy(
  () => import('@/views/spaceframe/SpaceframeWorkbenchView'),
);
// Land → Cross Kart (2 Seater): same configurator pattern, own store/spec —
// see docs/spaceframe/crosskart/PLAN.md.
const CrossKartWorkbenchView = lazy(
  () => import('@/views/crosskart/CrossKartWorkbenchView'),
);

export function PromptView({ isActive = true }: { isActive?: boolean } = {}) {
  const navigate = useNavigate();
  const { user, billing, isLoading } = useAuth();
  const isAnonymous = !user || user.is_anonymous;
  const totalTokens = billing?.tokens.total ?? 0;
  const isMobile = useIsMobile();

  const isLg = useMediaQuery('(min-width: 1024px)');

  const {
    vehicleEnabled,
    setVehicleEnabled,
    vehicleTab,
    setVehicleTab,
    vehicleSubTab,
    textToCadSelected,
    tubeframeParamsReady,
    setTubeframeParamsReady,
    tubeframeParams,
    setTubeframeParams,
    supinParamsReady,
    supinParams,
    suspensionKinematicsParamsReady,
    suspensionKinematicsParams,
    implicitPrimitive,
    implicitPrimitiveParams,
    supinInletType,
    model,
    setModel,
    outputFormat,
    setOutputFormat,
    type,
    setType,
    registerSidebarAction,
    featureTreeOpen,
  } = useHomepage();

  const handleTypeChange = (newType: 'parametric' | 'imagetocad') => {
    setType(newType);
    if (newType === 'imagetocad') {
      setModel('meshy7');
    } else {
      setModel('google/gemini-3.1-pro-preview');
    }
  };

  // The centred prompt card replaces the hero on the plain Text-or-Image-to-CAD
  // page, desktop only. The sub-tabs that live under textToCadSelected (the
  // Lattice Converter, Implicit Primitives, NASA Lattice) keep their own hero
  // and their own tree — they are separate surfaces, not the prompt page.
  const isPlainTextToCad =
    textToCadSelected &&
    vehicleSubTab !== 'nasa-unitcell-converter' &&
    vehicleSubTab !== 'implicit-primitives' &&
    vehicleSubTab !== 'nasa-lattice-primitives';
  const showCenteredPromptCard = isLg && isPlainTextToCad;

  const [isLoaded, setIsLoaded] = useState(false);
  const [images, setImages] = useState<MessageItem[]>([]);
  const [mesh, setMesh] = useState<MessageItem | null>(null);

  const isSupinInlet =
    vehicleEnabled &&
    vehicleTab === 'air' &&
    vehicleSubTab === 'nasa-supersonic-inlet';

  // NASA SUPIN license gate. SUPIN ships under a NASA Software Usage Agreement
  // with usage restrictions, so the prompt hero is replaced by a license screen
  // (restrictions + signed agreement) and Generate is password-gated whenever a
  // SUPIN inlet is selected. The 3-column gate is a desktop hero replacement;
  // unlock state is held for the session. See src/config/supinLicense.ts.
  const [supinLicenseUnlocked, setSupinLicenseUnlocked] = useState(false);
  const showSupinLicenseGate = isLg && isSupinInlet;
  const supinLicenseBlocked = isSupinInlet && !supinLicenseUnlocked;

  // JAWS Suspension Kinematics is NOT generative — it renders a dedicated,
  // persistent view that bypasses the prompt → Generate → chat/artifact
  // pipeline entirely. isSuspensionKinematics also matches the old sub-tab id
  // suspension-workbench (back-compat for state persisted under the
  // pre-rename name).
  const isSuspensionKinematics =
    vehicleEnabled &&
    vehicleTab === 'land' &&
    (vehicleSubTab === 'suspension-kinematics' ||
      vehicleSubTab === 'suspension-workbench');

  const isSpaceframe =
    vehicleEnabled && vehicleTab === 'land' && vehicleSubTab === 'spaceframe';

  const isCrossKart =
    vehicleEnabled && vehicleTab === 'land' && vehicleSubTab === 'cross-kart';

  const requiresSolidbody =
    (vehicleEnabled &&
      vehicleTab === 'space' &&
      vehicleSubTab === 'rocket-nozzle') ||
    isSupinInlet;

  const lowPrompts = useMemo(() => {
    if (isLoading) return false;
    return totalTokens > 0 && totalTokens <= 10;
  }, [totalTokens, isLoading]);

  const limitReached = useMemo(() => {
    if (isLoading) return false;
    // Only a CONFIRMED zero balance blocks generation. If billing is unknown —
    // `billing === null` while not loading, e.g. the billing-status invoke
    // errored out — fail OPEN: never hard-block a funded user we just failed to
    // price. (#176: a 100k-token user was shown limitReached because the billing
    // fetch transiently failed → billing null → totalTokens 0 → false positive.
    // Mirrors ParametricEditorView's `billing != null && totalTokens <= 0`.)
    return billing != null && totalTokens <= 0;
  }, [billing, totalTokens, isLoading]);

  const isTubeframeJoint =
    vehicleEnabled &&
    vehicleTab === 'land' &&
    vehicleSubTab === 'tubeframe-joint';

  // Land → Wheels — the Lunar Rover Wheel and any future wheel primitives.
  // Shares the implicitPrimitive/implicitPrimitiveParams pipeline with the
  // Implicit Primitives library tab, but lives under a vehicle domain so the
  // center hero swaps and the sidebar Generate fires via this branch.
  const isLandWheels =
    vehicleEnabled && vehicleTab === 'land' && vehicleSubTab === 'wheels';

  // Space → Heat Exchanger — the helix heat exchanger (built at 0.5mm, served as
  // fidelity tiers). Same implicit-primitive pipeline as Land → Wheels.
  const isSpaceHeatExchanger =
    vehicleEnabled &&
    vehicleTab === 'space' &&
    vehicleSubTab === 'heat-exchanger';

  // Vehicle-domain wrappers that drive the implicit-primitive pipeline (Generate
  // routes through implicitPrimitiveParams, center hero swaps to the part).
  const isVehicleImplicitPrimitive = isLandWheels || isSpaceHeatExchanger;

  const vehicleSubtype = isTubeframeJoint
    ? ('tubeframe-joint' as const)
    : isSupinInlet
      ? ('nasa-supersonic-inlet' as const)
      : isSuspensionKinematics
        ? ('suspension-kinematics' as const)
        : undefined;

  const { newConversationId, handleGenerate, isGenerating } =
    useGenerateConversation({ type, model, vehicleSubtype });

  // NASA Unitcell Lattice Converter — once a part is uploaded and measured by
  // lattice-analyze, params.previewUrl holds a signed, browser-renderable STL of
  // the ORIGINAL part. Swap the center hero for a live viewport of that part (a
  // top "Generating NASA Unitcell Lattice…" bar shows while a fill is running)
  // so the user sees their geometry the instant it's measured. #309.
  const latticePreviewUrl =
    implicitPrimitiveParams.kind === 'lattice-fill'
      ? implicitPrimitiveParams.params.previewUrl
      : '';
  // #315: B-rep edges of an uploaded STEP, shown on the original-part preview.
  const latticePreviewEdges =
    implicitPrimitiveParams.kind === 'lattice-fill'
      ? {
          edgesB64: implicitPrimitiveParams.params.edgesB64,
          tangentEdgesB64: implicitPrimitiveParams.params.tangentEdgesB64,
        }
      : undefined;
  const showLatticeViewport = latticePreviewUrl !== '';

  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      setIsLoaded(true);
    });
    return () => cancelAnimationFrame(frame);
  }, []);

  const textAreaChatProps = {
    onSubmit: (content: Parameters<typeof handleGenerate>[0]) => {
      if (isTubeframeJoint && tubeframeParams) {
        const { od, id, matingSurface, units } = tubeframeParams;
        const surfaceLabel: Record<string, string> = {
          'straight-wall': 'Straight Wall',
          lattice: 'Lattice',
          threaded: 'Threaded',
        };
        const unitSuffix = units === 'in' ? '"' : 'mm';
        handleGenerate({
          ...content,
          vehicleParams: tubeframeParams,
          text: `Design a Tubeframe Joint with ${od}${unitSuffix} OD ${id}${unitSuffix} ID and ${surfaceLabel[matingSurface] ?? matingSurface} Mating Surface`,
        });
      } else if (isSupinInlet && supinParams) {
        handleGenerate({
          ...content,
          supinParams,
          outputFormat: 'solidbody',
          text: `Generate NASA SUPIN ${INLET_LABELS[supinParams.inletType] ?? supinParams.inletType} supersonic inlet at Mach ${supinParams.mach}, ${supinParams.altitude} ft`,
        });
      } else if (isSuspensionKinematics && suspensionKinematicsParams) {
        handleGenerate({
          ...content,
          suspensionKinematicsParams,
          outputFormat: 'solidbody',
          text: `Analyze ${suspensionKinematicsParams.axle} ${SUSP_TYPE_LABELS[suspensionKinematicsParams.suspType] ?? suspensionKinematicsParams.suspType} suspension kinematics`,
        });
      } else if (isVehicleImplicitPrimitive) {
        const label = IMPLICIT_PRIMITIVE_LABELS[implicitPrimitive];
        handleGenerate({
          ...content,
          outputFormat: 'implicit',
          text: `Generate ${label}`,
          implicitPrimitiveParams: {
            kind: implicitPrimitiveParams.kind,
            params: implicitPrimitiveParams.params as unknown as Record<
              string,
              string
            >,
          },
        });
      } else {
        handleGenerate(content);
      }
    },
    conversation: { id: newConversationId, user_id: user?.id ?? '' },
    onFocus: () => {},
    placeholder: isMobile
      ? 'Use Telekinesis to move matter with your mind... (Open in Desktop for Full Features.)'
      : 'Use Telekinesis to move matter with your mind.',
    type,
    disabled: limitReached,
    model,
    setModel,
    showPromptGenerator: true,
    showFullLabels: true,
    onTypeChange: handleTypeChange,
    requiresSolidbody,
    vehicleParamsReady:
      (vehicleEnabled &&
        vehicleTab === 'space' &&
        vehicleSubTab === 'rocket-nozzle') ||
      tubeframeParamsReady ||
      supinParamsReady ||
      suspensionKinematicsParamsReady ||
      isVehicleImplicitPrimitive,
    initialOutputFormat: outputFormat,
    onOutputFormatChange: setOutputFormat,
  } as const;

  usePromptSidebarActions({
    isMobile,
    vehicleEnabled,
    vehicleTab,
    vehicleSubTab,
    textToCadSelected,
    tubeframeParamsReady,
    tubeframeParams,
    supinParamsReady,
    supinParams,
    suspensionKinematicsParamsReady,
    suspensionKinematicsParams,
    isTubeframeJoint,
    isSupinInlet,
    isSuspensionKinematics,
    isVehicleImplicitPrimitive,
    implicitPrimitive,
    implicitPrimitiveParams,
    model,
    outputFormat,
    isGenerating,
    limitReached,
    supinLicenseBlocked,
    handleGenerate,
    registerSidebarAction,
  });

  // Dedicated non-generative views for the Suspension Kinematics sub-features.
  // Placed after all hooks (rules-of-hooks) and before the generative UI.
  if (isSuspensionKinematics)
    return (
      <Suspense fallback={<ViewerRouteFallback />}>
        {/* `active` gates the shared-slot portal so only the active tab's
            workbench fills the feature tree (no doubling across mounted tabs). */}
        <SuspensionKinematics active={isActive} />
      </Suspense>
    );

  // Spaceframe Chassis: deterministic FIA driver-cell configurator — no prompt,
  // no Generate; the workbench IS the view.
  if (isSpaceframe)
    return (
      <Suspense fallback={<ViewerRouteFallback />}>
        <SpaceframeWorkbenchView active={isActive} />
      </Suspense>
    );

  // Cross Kart (2 Seater): same deterministic-configurator posture as
  // Spaceframe Chassis — no prompt, no Generate; the workbench IS the view.
  if (isCrossKart)
    return (
      <Suspense fallback={<ViewerRouteFallback />}>
        <CrossKartWorkbenchView active={isActive} />
      </Suspense>
    );

  // NASA Unitcell Lattice Converter — once a part is uploaded + measured
  // (params.previewUrl set), render the editor's OWN dual-pane layout (viewer +
  // chat) in place of the marketing hero, with NO logo/hero/subtitle. The
  // pre-generation standby then matches the editor exactly and continues
  // seamlessly into it on Generate (same components, same conversation id). The
  // marketing layout below is skipped entirely while a converter part is loaded.
  // The viewer fills the full editor-height pane (h-full off the tab container),
  // not the old centered, scrolling, clipped `h-[70vh]` hero slot. #309.
  if (showLatticeViewport) {
    return (
      <div className="relative h-full w-full overflow-hidden bg-[#292828]">
        {/* Same feature-window host as the marketing hero, so the converter's
            params window (upload + lattice sliders) keeps floating over the
            uploaded-part preview after upload (this early return replaces the
            marketing layout + its host). Desktop only. */}
        {isLg && <FeatureWindowHost />}
        <Suspense
          fallback={
            <div className="flex h-full w-full items-center justify-center">
              <p className="text-sm text-telekinesis-neutral-400">
                Loading viewer…
              </p>
            </div>
          }
        >
          <LatticeConverterViewport
            previewUrl={latticePreviewUrl}
            previewEdges={latticePreviewEdges}
            conversationId={newConversationId}
            isMobile={isMobile}
          />
        </Suspense>
      </div>
    );
  }

  return (
    <div className="relative min-h-full w-full bg-telekinesis-bg-secondary-dark">
      {isAnonymous && (
        <div className="fixed left-1/2 top-4 z-10 flex -translate-x-1/2 flex-row gap-2 md:left-auto md:right-4 md:translate-x-0">
          <Button
            variant="light"
            onClick={() => navigate('/signup')}
            className="w-auto"
          >
            Sign Up
          </Button>
          <Button onClick={() => navigate('/signin')} className="w-auto">
            <LogIn className="mr-2 h-4 w-4" />
            Sign In
          </Button>
        </div>
      )}

      <main className="relative flex w-full flex-col items-center justify-start px-4 pt-8 md:px-8 md:pt-10">
        {/* Feature-tree windows (desktop only — mobile uses ParameterArea and
            has no feature tree). The home page has no viewer, so the floating
            params window for the selected feature floats over this `relative`
            main area instead — same "select a feature → its window opens"
            behaviour as the editor's viewport host. */}
        {isLg && <FeatureWindowHost />}
        {/* NASA SUPIN license gate — signed-agreement slideshow pinned to the
            right of the hero; the access box (below) password-gates Generate. */}
        {showSupinLicenseGate && <SupinLicenseSlideshow />}
        {/* Viewport-centered block: logo + hero + subtitle shift left by half sidebar width */}
        <div
          className="flex w-full flex-col items-center transition-transform duration-200"
          style={{
            transform: isLg
              ? `translateX(${featureTreeOpen ? '-4.1875rem' : '-0.1875rem'})`
              : undefined,
          }}
        >
          {/* SUPIN access box — in-flow above the logo so it shares the same
              centered axis (and sidebar shift) as the hero. */}
          {showSupinLicenseGate && (
            <SupinLicenseAccessBox
              unlocked={supinLicenseUnlocked}
              onUnlockedChange={setSupinLicenseUnlocked}
            />
          )}
          {/* Logo + title — constrained to max-w-3xl */}
          <PromptLogo isLoaded={isLoaded} />

          {/* Text-or-Image-to-CAD, desktop: the prompt IS the page. The card
              takes the hero's slot and the feature tree below the Model/Kernel
              selectors renders empty (FeatureTreeSidebar) — the prompt is no
              longer a sidebar control. Every OTHER domain keeps its hero and
              its parameter tree exactly as before, and mobile is untouched
              (it still renders the inline TextAreaChat below). */}
          {showCenteredPromptCard && (
            <div
              className={cn(
                'mb-3 flex w-full justify-center',
                'motion-safe:transition-opacity motion-safe:duration-500 motion-safe:ease-out',
                isLoaded ? 'opacity-100' : 'opacity-0',
              )}
            >
              <PromptCard />
            </div>
          )}

          {/* Hero — full content width so centering is correct between
              sidebars. (The NASA Unitcell Lattice Converter standby is handled
              by the full-pane early return above, so the hero is never the
              converter viewport here.) */}
          <div
            className={cn(
              'mb-3 flex w-full justify-center',
              'motion-safe:transition-opacity motion-safe:duration-500 motion-safe:ease-out',
              isLoaded ? 'opacity-100' : 'opacity-0',
              showCenteredPromptCard && 'hidden',
            )}
          >
            <PromptHero
              textToCadSelected={textToCadSelected}
              vehicleSubTab={vehicleSubTab}
              implicitPrimitive={implicitPrimitive}
              vehicleEnabled={vehicleEnabled}
              isLandWheels={isLandWheels}
              isSpaceHeatExchanger={isSpaceHeatExchanger}
              vehicleTab={vehicleTab}
              supinInletType={supinInletType}
              isMobile={isMobile}
            />
          </div>

          {/* Subtitle + mid-range marketplace button. Suppressed under the
              centred card — the card already carries the page's message and
              the subtitle would push it below the fold. */}
          {!showCenteredPromptCard && (
            <PromptSubtitle
              isLoaded={isLoaded}
              onMarketplaceClick={() => navigate('/market')}
            />
          )}
        </div>
        {/* end viewport-centering wrapper */}

        <div className="flex w-full flex-col items-center">
          <div className="w-full max-w-3xl space-y-4 pb-12">
            {/* Inline TextAreaChat — mobile only (desktop uses sidebar or FeatureTreeSidebar) */}
            {isMobile && (
              <SelectedItemsContext.Provider
                value={{ images, setImages, mesh, setMesh }}
              >
                <TextAreaChat {...textAreaChatProps} />
              </SelectedItemsContext.Provider>
            )}

            {/* ParameterArea — mobile only on home page; desktop uses
                FeatureTreeSidebar. Conditional mount (not just CSS-hidden) so
                its tab forms never double-mount with the desktop sidebar and
                race on the shared *ParamsReady / *Params context (which left the
                sidebar Generate button stuck disabled). NASA SUPIN is desktop-
                only and is not offered here. */}
            {!isLg && (
              <ParameterArea
                enabled={vehicleEnabled}
                onEnabledChange={setVehicleEnabled}
                activeTab={vehicleTab}
                onTabChange={setVehicleTab}
                onParamsReadyChange={setTubeframeParamsReady}
                onTubeframeParamsChange={setTubeframeParams}
              />
            )}

            {/* Mobile marketplace button */}
            <button
              onClick={() => navigate('/market')}
              className="flex w-full items-center rounded-xl border border-telekinesis-neutral-700 bg-telekinesis-bg-dark px-5 py-3 transition-colors hover:border-telekinesis-gold hover:bg-telekinesis-neutral-900 md:hidden"
            >
              <span className="h-4 w-4 shrink-0" />
              <div className="mx-4 flex-1 text-center">
                <p className="text-sm font-semibold text-[#107d40]">
                  Marketplace
                </p>
                <p className="text-xs text-telekinesis-text-secondary">
                  Buy and Sell Designs, 3D Printed &amp; shipped to your door.
                </p>
              </div>
              <ArrowRight className="h-4 w-4 shrink-0 text-telekinesis-text-secondary" />
            </button>
            <div>
              {/* Billing-gated messages render only after billing resolves
                  (isLoading false) to avoid an off-center spinner flash;
                  full-screen auth gates elsewhere already handle the load state. */}
              {!isLoading && limitReached && <LimitReachedMessage />}
              {!isLoading && lowPrompts && !limitReached && (
                <LowPromptsWarningMessage tokensRemaining={totalTokens} />
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
